import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Loader2, Repeat2, Star, Wallet, X } from "lucide-react";
import { convertCashToCoin, convertCoinToCash } from "../../api/conversionApi";
import { getApiErrorMessage } from "../../utils/paymentHelpers";
import { COIN_RATE } from "../../utils/walletAnalytics";
import "./payments.css";

const DIRECTIONS = {
  buy: {
    title: "Buy Coin",
    subtitle: `Convert Cash to Coin · 1 Cash = ${COIN_RATE} Coin`,
    fromLabel: "Cash",
    toLabel: "Coin",
    FromIcon: Wallet,
    ToIcon: Star,
  },
  swap: {
    title: "Swap Coin to Cash",
    subtitle: `Convert Coin back to Cash · ${COIN_RATE} Coin = 1 Cash`,
    fromLabel: "Coin",
    toLabel: "Cash",
    FromIcon: Star,
    ToIcon: Wallet,
  },
};

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const ConvertModal = ({
  open,
  direction = "buy",
  onClose,
  userId,
  cash = 0,
  coin = 0,
  onNotify,
  onSuccess,
}) => {
  const titleId = useId();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cfg = DIRECTIONS[direction] || DIRECTIONS.buy;
  const isBuy = direction === "buy";
  const available = isBuy ? cash : coin;

  useEffect(() => {
    if (!open) return undefined;
    setAmount("");
    setError("");

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, direction]);

  const numAmount = Number(amount) || 0;
  const receiveAmount = isBuy ? numAmount * COIN_RATE : numAmount / COIN_RATE;

  const quickAmounts = useMemo(() => {
    if (isBuy) {
      return [100, 500, 1000]
        .filter((v) => v <= cash)
        .concat(cash >= 1 ? [Math.floor(cash)] : [])
        .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
        .slice(0, 4);
    }
    const maxSwappable = Math.floor(coin / COIN_RATE) * COIN_RATE;
    return [100, 500, 1000]
      .filter((v) => v <= maxSwappable)
      .concat(maxSwappable >= COIN_RATE ? [maxSwappable] : [])
      .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
      .slice(0, 4);
  }, [isBuy, cash, coin]);

  if (!open) return null;

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!numAmount || numAmount <= 0) {
      setError("Enter a valid amount greater than 0.");
      return;
    }
    if (numAmount > available) {
      setError(`Insufficient ${cfg.fromLabel} balance.`);
      return;
    }
    if (!isBuy && numAmount % COIN_RATE !== 0) {
      setError(`Coin amount must be divisible by ${COIN_RATE} (${COIN_RATE} Coin = 1 Cash).`);
      return;
    }
    if (!userId) {
      setError("Sign in again to convert.");
      return;
    }

    try {
      setLoading(true);
      const data = isBuy
        ? await convertCashToCoin({ userId, cashAmount: numAmount })
        : await convertCoinToCash({ userId, coinAmount: numAmount });

      if (!data?.success) {
        const message = data?.message || "Conversion failed.";
        setError(message);
        onNotify?.("error", "Conversion failed", message);
        return;
      }

      onNotify?.(
        "success",
        isBuy ? "Coin purchased" : "Coin swapped",
        `${fmt(numAmount)} ${cfg.fromLabel} → ${fmt(receiveAmount)} ${cfg.toLabel}`
      );
      onSuccess?.(data);
      handleClose();
    } catch (err) {
      const message = getApiErrorMessage(err, "Unable to process conversion");
      setError(message);
      onNotify?.("error", "Conversion failed", message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="pay-modal-root" role="presentation">
      <button
        type="button"
        className="pay-modal-backdrop"
        aria-label="Close dialog"
        disabled={loading}
        onClick={handleClose}
      />

      <form
        className="pay-modal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="pay-modal-head">
          <span className="pay-modal-head-icon" aria-hidden>
            <Repeat2 size={16} />
          </span>
          <div className="pay-modal-head-copy">
            <h2 id={titleId}>{cfg.title}</h2>
            <p>{cfg.subtitle}</p>
          </div>
          <button
            type="button"
            className="pay-modal-close"
            aria-label="Close"
            disabled={loading}
            onClick={handleClose}
          >
            <X size={14} />
          </button>
        </header>

        <div className="pay-modal-body">
          <p className="pay-modal-hint">
            Available: {fmt(available)} {cfg.fromLabel}. Conversions are instant and free.
          </p>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-convert-amount">
              Amount ({cfg.fromLabel})
            </label>
            <div className="pay-modal-input-wrap">
              <span className="pay-modal-prefix">{cfg.fromLabel}</span>
              <input
                id="pay-convert-amount"
                type="number"
                min="1"
                step={isBuy ? "1" : String(COIN_RATE)}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Enter amount"
                disabled={loading}
              />
            </div>
          </div>

          {quickAmounts.length > 0 && (
            <div className="pay-modal-field">
              <span className="pay-modal-label">Quick amounts</span>
              <div className="pay-modal-methods">
                {quickAmounts.map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    className={`pay-modal-method${Number(amount) === v ? " pay-modal-method-active" : ""}`}
                    onClick={() => setAmount(String(v))}
                    disabled={loading}
                  >
                    {i === quickAmounts.length - 1 && v === quickAmounts[quickAmounts.length - 1] && v > 1000
                      ? `Max ${fmt(v)}`
                      : fmt(v)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pay-convert-preview" aria-live="polite">
            <span className="pay-convert-side">
              <cfg.FromIcon size={13} aria-hidden />
              <strong className="tabular-nums">{fmt(numAmount)}</strong>
              <em>{cfg.fromLabel}</em>
            </span>
            <ArrowRight size={14} className="pay-convert-arrow" aria-hidden />
            <span className="pay-convert-side pay-convert-side-to">
              <cfg.ToIcon size={13} aria-hidden />
              <strong className="tabular-nums">{fmt(receiveAmount)}</strong>
              <em>{cfg.toLabel}</em>
            </span>
          </div>

          {error ? <p className="pay-modal-error">{error}</p> : null}
        </div>

        <div className="pay-modal-foot">
          <button type="submit" className="pay-modal-submit" disabled={loading || !numAmount}>
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Converting…
              </>
            ) : (
              cfg.title
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default ConvertModal;
